import { AuthError } from '@supabase/supabase-js';

export type AuthErrorType =
  | 'auth_invalid_credentials'
  | 'auth_user_not_found'
  | 'auth_email_not_confirmed'
  | 'auth_too_many_requests'
  | 'auth_signup_disabled'
  | 'auth_weak_password'
  | 'auth_email_rate_limit'
  | 'auth_provider_error'
  | 'auth_network_error'
  | 'auth_session_expired'
  | 'auth_permission_denied'
  | 'auth_invalid_token'
  | 'auth_user_already_exists'
  | 'auth_unknown_error'
  | 'auth_configuration_error';

export interface AuthErrorInfo {
  type: AuthErrorType;
  message: string;
  userMessage: string;
  action?: 'retry' | 'redirect' | 'contact_support' | 'wait' | 'none';
  actionLabel?: string;
  actionUrl?: string;
  retryAfter?: number; // seconds
}

export interface ErrorDisplayOptions {
  showDetails?: boolean;
  showAction?: boolean;
  autoHide?: boolean;
  hideAfter?: number; // milliseconds
  position?: 'top' | 'bottom' | 'center';
}

/**
 * Comprehensive error handling system for authentication
 * Provides user-friendly error messages and suggested actions
 */
export class AuthErrorHandler {
  private static instance: AuthErrorHandler;

  private readonly errorMap: Record<string, Partial<AuthErrorInfo>> = {
    // Supabase Auth API errors
    'Authentication service is not configured': {
      type: 'auth_configuration_error',
      userMessage: 'Sign-in is temporarily unavailable. Please try again later or contact support.',
      action: 'contact_support',
      actionLabel: 'Contact Support',
    },
    'Invalid login credentials': {
      type: 'auth_invalid_credentials',
      userMessage:
        'The email or password you entered is incorrect. Please check your credentials and try again.',
      action: 'retry',
      actionLabel: 'Try Again',
    },
    'Email not confirmed': {
      type: 'auth_email_not_confirmed',
      userMessage:
        'Please check your email and click the confirmation link before signing in.',
      action: 'redirect',
      actionLabel: 'Resend Confirmation',
      actionUrl: '/auth/resend-confirmation',
    },
    'User already registered': {
      type: 'auth_user_already_exists',
      userMessage:
        'An account with this email already exists. Try signing in instead.',
      action: 'redirect',
      actionLabel: 'Sign In',
      actionUrl: '/auth/login',
    },
    'User not found': {
      type: 'auth_user_not_found',
      userMessage:
        'No account found with this email address. Please check your email or create a new account.',
      action: 'redirect',
      actionLabel: 'Create Account',
      actionUrl: '/auth/signup',
    },
    'Password should be at least 6 characters': {
      type: 'auth_weak_password',
      userMessage:
        'Your password must be at least 6 characters long. Please choose a stronger password.',
      action: 'retry',
      actionLabel: 'Update Password',
    },
    'Too many requests': {
      type: 'auth_too_many_requests',
      userMessage:
        'Too many attempts. Please wait a moment before trying again.',
      action: 'wait',
    },
    'Email rate limit exceeded': {
      type: 'auth_email_rate_limit',
      userMessage:
        "We've sent too many emails to this address recently. Please wait before requesting another.",
      action: 'wait',
    },
    'Signups not allowed': {
      type: 'auth_signup_disabled',
      userMessage:
        'New account registration is currently unavailable. Please try again later.',
      action: 'contact_support',
      actionLabel: 'Contact Support',
    },
    'Invalid session': {
      type: 'auth_session_expired',
      userMessage: 'Your session has expired. Please sign in again.',
      action: 'redirect',
      actionLabel: 'Sign In',
      actionUrl: '/auth/login',
    },
    'Invalid JWT': {
      type: 'auth_invalid_token',
      userMessage: 'Your session is invalid. Please sign in again.',
      action: 'redirect',
      actionLabel: 'Sign In',
      actionUrl: '/auth/login',
    },
    'Permission denied': {
      type: 'auth_permission_denied',
      userMessage: "You don't have permission to perform this action.",
      action: 'contact_support',
      actionLabel: 'Contact Support',
    },

    // OAuth-specific errors
    'OAuth provider error': {
      type: 'auth_provider_error',
      userMessage:
        'There was an issue with the authentication provider. Please try again or use a different sign-in method.',
      action: 'retry',
      actionLabel: 'Try Again',
    },
    access_denied: {
      type: 'auth_provider_error',
      userMessage:
        'You cancelled the authentication process. Please try again to continue.',
      action: 'retry',
      actionLabel: 'Try Again',
    },
    invalid_request: {
      type: 'auth_provider_error',
      userMessage:
        'There was an error in the authentication request. Please try again.',
      action: 'retry',
      actionLabel: 'Try Again',
    },

    // Network errors
    'Network request failed': {
      type: 'auth_network_error',
      userMessage:
        'Network connection failed. Please check your internet connection and try again.',
      action: 'retry',
      actionLabel: 'Retry',
    },
    'Failed to fetch': {
      type: 'auth_network_error',
      userMessage:
        'Unable to connect to the server. Please check your internet connection.',
      action: 'retry',
      actionLabel: 'Retry',
    },
  };

  private constructor() {}

  public static getInstance(): AuthErrorHandler {
    if (!AuthErrorHandler.instance) {
      AuthErrorHandler.instance = new AuthErrorHandler();
    }
    return AuthErrorHandler.instance;
  }

  /**
   * Handle authentication error and return user-friendly information
   * Sanitizes error messages in production to prevent information disclosure
   */
  handleError(error: AuthError | Error | string | null): AuthErrorInfo {
    if (!error) {
      return this.getDefaultError();
    }

    const errorMessage =
      typeof error === 'string' ? error : error.message || 'Unknown error';

    const errorCode =
      typeof error !== 'string' && 'code' in error && typeof error.code === 'string'
        ? error.code
        : undefined;
    const status =
      typeof error !== 'string' && 'status' in error && typeof error.status === 'number'
        ? error.status
        : undefined;

    // Never log raw upstream messages, credentials, account identifiers or stacks.
    if (import.meta.env.DEV) {
      console.error('Auth error (development):', { code: errorCode, status });
    } else {
      console.error('Authentication failed:', { code: errorCode || 'unknown', status });
    }

    const errorInfo = this.findErrorInfo(errorMessage, errorCode, status);

    // Sanitize the returned error for production
    const sanitizedMessage = import.meta.env.PROD
      ? this.sanitizeErrorMessage(errorMessage)
      : errorMessage;

    return {
      type: 'auth_unknown_error',
      message: sanitizedMessage,
      userMessage: 'An unexpected error occurred. Please try again.',
      action: 'retry',
      actionLabel: 'Try Again',
      ...errorInfo,
    };
  }

  /**
   * Find error information based on error message
   */
  private findErrorInfo(
    errorMessage: string,
    errorCode?: string,
    status?: number,
  ): Partial<AuthErrorInfo> {
    // An HTTP 429 is a throttle even when the upstream error code is misleading.
    if (status === 429) return this.errorMap['Too many requests'];

    // Prefer documented Supabase error codes over changeable message text.
    switch (errorCode) {
      case 'invalid_credentials':
        return this.errorMap['Invalid login credentials'];
      case 'email_not_confirmed':
        return this.errorMap['Email not confirmed'];
      case 'over_request_rate_limit':
        return this.errorMap['Too many requests'];
      case 'over_email_send_rate_limit':
        return this.errorMap['Email rate limit exceeded'];
      case 'user_already_exists':
      case 'email_exists':
        return this.errorMap['User already registered'];
      case 'signup_disabled':
      case 'email_provider_disabled':
        return this.errorMap['Signups not allowed'];
      case 'flow_state_expired':
      case 'flow_state_not_found':
      case 'session_expired':
        return this.errorMap['Invalid session'];
      case 'request_timeout':
        return this.errorMap['Network request failed'];
    }

    // Try exact match first
    if (this.errorMap[errorMessage]) {
      return this.errorMap[errorMessage];
    }

    // Try partial matches
    for (const [pattern, info] of Object.entries(this.errorMap)) {
      if (errorMessage.toLowerCase().includes(pattern.toLowerCase())) {
        return info;
      }
    }

    // Check for specific patterns
    if (
      errorMessage.includes('rate limit') ||
      errorMessage.includes('too many') ||
      errorMessage.includes('attempts remaining')
    ) {
      return this.errorMap['Too many requests'];
    }

    if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
      return this.errorMap['Network request failed'];
    }

    if (errorMessage.includes('password') && errorMessage.includes('weak')) {
      return this.errorMap['Password should be at least 6 characters'];
    }

    if (errorMessage.includes('session') || errorMessage.includes('expired')) {
      return this.errorMap['Invalid session'];
    }

    return {};
  }

  /**
   * Sanitize error message for production
   */
  private sanitizeErrorMessage(message: string): string {
    // Remove potentially sensitive information in production
    const sensitivePatterns = [
      /localhost:\d+/gi,
      /\b\d+\.\d+\.\d+\.\d+\b/g, // IP addresses
      /\b[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}\b/g, // Email addresses
      /\btoken\s*[:\=]\s*[a-zA-Z0-9_\-\.]+/gi, // Tokens
      /\bkey\s*[:\=]\s*[a-zA-Z0-9_\-\.]+/gi, // Keys
      /\bpassword\s*[:\=]\s*\S+/gi, // Passwords
      /\bid\s*[:\=]\s*[a-zA-Z0-9\-]+/gi, // IDs
      /\bstack\s*trace/gi, // Stack traces
      /\bat\s+[\w\.]+\s*\([^)]+\)/gi, // Stack trace lines
    ];

    let sanitized = message;
    sensitivePatterns.forEach(pattern => {
      sanitized = sanitized.replace(pattern, '[REDACTED]');
    });

    // Truncate very long messages
    if (sanitized.length > 200) {
      sanitized = sanitized.substring(0, 200) + '...';
    }

    return sanitized;
  }

  /**
   * Get default error for unknown cases
   */
  private getDefaultError(): AuthErrorInfo {
    return {
      type: 'auth_unknown_error',
      message: import.meta.env.PROD
        ? 'Authentication error'
        : 'Unknown error occurred',
      userMessage:
        'Something went wrong. Please try again or contact support if the problem persists.',
      action: 'retry',
      actionLabel: 'Try Again',
    };
  }

  /**
   * Check if error is recoverable (user can retry)
   */
  isRecoverable(errorInfo: AuthErrorInfo): boolean {
    const recoverableTypes: AuthErrorType[] = [
      'auth_invalid_credentials',
      'auth_weak_password',
      'auth_network_error',
      'auth_provider_error',
    ];

    return recoverableTypes.includes(errorInfo.type);
  }

  /**
   * Check if error requires immediate action
   */
  requiresImmediateAction(errorInfo: AuthErrorInfo): boolean {
    const immediateActionTypes: AuthErrorType[] = [
      'auth_session_expired',
      'auth_invalid_token',
      'auth_email_not_confirmed',
    ];

    return immediateActionTypes.includes(errorInfo.type);
  }

  /**
   * Get retry delay for rate limited errors
   */
  getRetryDelay(errorInfo: AuthErrorInfo): number {
    return errorInfo.retryAfter || 0;
  }

  /**
   * Format error for display
   */
  formatErrorMessage(
    errorInfo: AuthErrorInfo,
    options: ErrorDisplayOptions = {}
  ): string {
    const { showDetails = false } = options;

    let message = errorInfo.userMessage;

    if (showDetails && !import.meta.env.PROD && errorInfo.message !== errorInfo.userMessage) {
      message += ` (${errorInfo.message})`;
    }

    if (errorInfo.retryAfter) {
      const minutes = Math.ceil(errorInfo.retryAfter / 60);
      message += ` Please wait ${minutes} minute${minutes > 1 ? 's' : ''} before trying again.`;
    }

    return message;
  }

  /**
   * Get suggested action for error
   */
  getSuggestedAction(errorInfo: AuthErrorInfo): {
    action: string;
    label: string;
    url?: string;
  } | null {
    if (!errorInfo.action || errorInfo.action === 'none') {
      return null;
    }

    return {
      action: errorInfo.action,
      label: errorInfo.actionLabel || 'Try Again',
      url: errorInfo.actionUrl,
    };
  }

  /**
   * Add custom error mapping
   */
  addErrorMapping(pattern: string, errorInfo: Partial<AuthErrorInfo>): void {
    this.errorMap[pattern] = errorInfo;
  }

  /**
   * Get error statistics for monitoring
   */
  getErrorStats(): Record<AuthErrorType, number> {
    // In a real implementation, this would track error occurrences
    // For now, return empty stats
    return {} as Record<AuthErrorType, number>;
  }
}

// Export singleton instance
export const authErrorHandler = AuthErrorHandler.getInstance();

// Convenience functions
export const handleAuthError = (error: AuthError | Error | string | null) =>
  authErrorHandler.handleError(error);

export const isRecoverableError = (errorInfo: AuthErrorInfo) =>
  authErrorHandler.isRecoverable(errorInfo);

export const requiresImmediateAction = (errorInfo: AuthErrorInfo) =>
  authErrorHandler.requiresImmediateAction(errorInfo);

export const formatAuthError = (
  errorInfo: AuthErrorInfo,
  options?: ErrorDisplayOptions
) => authErrorHandler.formatErrorMessage(errorInfo, options);

export const getSuggestedAction = (errorInfo: AuthErrorInfo) =>
  authErrorHandler.getSuggestedAction(errorInfo);

/**
 * Fail closed if Supabase client configuration is missing or a fallback.
 * Local HTTP is allowed only for loopback development.
 */
export function isPublicAuthConfigured(
  url: string | undefined,
  key: string | undefined,
  development = false,
): boolean {
  if (!url || !key || key.length < 10 ||
      /placeholder|development-key/i.test(key) ||
      key.startsWith('sb_secret_') || /placeholder/i.test(url)) return false;

  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' ||
      (development && parsed.protocol === 'http:' &&
        ['localhost', '127.0.0.1', '[::1]'].includes(parsed.hostname));
  } catch {
    return false;
  }
}
